import type React from "react";

export interface FormTheme {
  version: 1;
  accent: string;
  background: { type: "solid" | "gradient"; color: string; endColor: string; angle: number };
}

export const DEFAULT_CUSTOM_THEME: FormTheme = {
  version: 1,
  accent: "#16a34a",
  background: { type: "gradient", color: "#ecfdf5", endColor: "#eff6ff", angle: 135 },
};

const hexToRgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const channel = (n: number) => { const v = n / 255; return v <= .03928 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4; };
const luminance = (hex: string) => { const [r, g, b] = hexToRgb(hex).map(channel); return .2126 * r + .7152 * g + .0722 * b; };
const readableText = (hex: string) => luminance(hex) > .42 ? "#0f172a" : "#ffffff";
const mix = (a: string, b: string, amount: number) => {
  const aa = hexToRgb(a), bb = hexToRgb(b);
  return `#${aa.map((v, i) => Math.round(v + (bb[i] - v) * amount).toString(16).padStart(2, "0")).join("")}`;
};

/** Preview tokens use a stronger derived action shade, never the raw user color blindly. */
export const formThemePreviewStyle = (theme: FormTheme | null, dark: boolean): React.CSSProperties | undefined => {
  if (!theme || !dark) return undefined;
  const primary = mix(theme.accent, dark ? "#ffffff" : "#000000", dark ? .08 : .12);
  const page = theme.background.type === "gradient"
    ? `linear-gradient(${theme.background.angle}deg, ${theme.background.color}, ${theme.background.endColor})`
    : theme.background.color;
  return {
    "--preview-page": page,
    "--preview-primary": primary,
    "--preview-primary-soft": `${primary}24`,
    "--preview-primary-border": `${primary}70`,
    "--preview-primary-foreground": readableText(primary),
    "--preview-card": dark ? "#0f172aeF" : "#fffffff2",
    "--preview-text": dark ? "#f8fafc" : "#0f172a",
    "--preview-muted": dark ? "#94a3b8" : "#64748b",
    "--preview-border": dark ? "#334155" : "rgba(100,116,139,.28)",
  } as React.CSSProperties;
};

export const isHexColor = (value: string) => /^#[0-9a-f]{6}$/i.test(value);
